import { ArrayNotEmpty, IsEmail, IsNotEmpty, IsOptional, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import agentUsersListToCsv from './agentUsersListToCsv';
import agentProfilesResource from 'src/actions/agent/agentProfilesResource';
import { AgentVoiceConfiguration } from 'src/actions/agent/agentProfileResource';
import createCollectionResource from 'src/actions/shared/createResource';
import { CsvUploadStatus } from 'src/models/Users/CsvUpload';
import emptyObserverCreator from 'src/actions/shared/emptyObserverCreator';
import exportUsersToCSV from 'src/actions/agentUsers/exportUsersToCsv';
import { IsStringPropertyUniqueInStoreSlice } from 'src/actions/validators/IsStringPropertyUniqueInStoreSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import skillHierarchyResource from 'src/actions/skills/skillHierarchyResource';
import skillsResource from 'src/actions/skills/skillsResource';
import { STATUS_UNSTARTED } from 'src/presentation/hooks/useResource';
import teamsResource from 'src/actions/teams/teamsResource';
import { toastError } from 'src/actions/toast';
import usersResource from 'src/actions/users/usersResource';
import { DomainError } from '@ad-scripts/application/lib/error';

export enum UserRoles {
  AGENT = 'agent',
  AGENT_PLUS = 'agent-plus',
  ANALYST = 'analyst',
  ANSWER_ADMIN = 'answers-admin',
  API_USER = 'api-user',
  COMPLIANCE_ADMIN = 'compliance-admin',
  DEVELOPER = 'developer',
  LIVEBOARDS = 'liveboards',
  SYSTEM_ADMIN = 'system-admin',
  TASK_USER = 'task-user',
  TEAM_MANAGER = 'team-manager',
}

export class UserAgent {
  id: string;

  @IsOptional()
  routingGroupIds: Array<string>;

  @ArrayNotEmpty({ message: 'At least one role should be selected' })
  roleIds: Array<string>;

  @IsEmail({}, { message: 'Enter an email.' })
  @IsNotEmpty({ message: 'Enter a valid email address.' })
  @IsStringPropertyUniqueInStoreSlice('agentProfiles', { message: 'Email address taken. Try another.' })
  email: string;

  name: string;

  @IsOptional()
  skillIds: Array<string>;

  @IsOptional()
  teamIds: Array<string>;

  @IsOptional()
  @Type(() => AgentVoiceConfiguration)
  @ValidateNested({ each: false })
  voiceConfiguration: AgentVoiceConfiguration;
}

const agentUsersResource = createCollectionResource<UserAgent>({
  name: 'agentUsers',
  endpoint: 'v3/orgs/:orgId/agent-users/:id',
  observerCreator: emptyObserverCreator,
  type: UserAgent,
  options: {
    toastMessages: {
      onAdd: () => `Added user profile`,
      onUpdate: ({ name, email }) => `Updated user profile for "${name || email}"`,
    },
  },
  reducers: {
    usersCsvUploadStatus(state, action) {
      state.usersCsvUploadStatus = action.payload;
    },
    usersCsvUploadState(state, action: PayloadAction<CsvUploadStatus>) {
      state.usersCsvUploadState = action.payload;
    },
    usersCsvUploadReset(state) {
      state.usersCsvUploadStatus = CsvUploadStatus.NotStarted;
      state.usersCsvUploadState = undefined;
    },
  },
});

const uploadCsvFile: sharedTypes.AppThunk =
  (file: File) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(agentUsersResource.actions.usersCsvUploadStatus(CsvUploadStatus.Started));
      const res = await gateways.agentUserUploads.uploadCsv({ file });
      dispatch(agentUsersResource.actions.usersCsvUploadState(res));

      if (!res.errors) {
        dispatch(agentUsersResource.actions.usersCsvUploadStatus(CsvUploadStatus.Success));
      } else {
        dispatch(agentUsersResource.actions.usersCsvUploadStatus(CsvUploadStatus.CompletedWithErrors));
      }
    } catch (err) {
      if ((err as DomainError).detailedMessage === 'email or roles field is missing in csv') {
        dispatch(agentUsersResource.actions.usersCsvUploadStatus(CsvUploadStatus.IncorrectFormatError));
      } else {
        dispatch(agentUsersResource.actions.usersCsvUploadStatus(CsvUploadStatus.ServerError));
      }
    }
  };

const downloadCsvFile: sharedTypes.AppThunk =
  allRoles =>
  async (dispatch, getState, { gateways }) => {
    const state = getState();

    try {
      await startLoadingRequiredResources(dispatch, state);
      await dispatch(skillsResource.effects.fetchAll());

      const users = state.users.values;
      const agentProfiles = state.agentProfiles.values;
      const teams = state.teams.values;
      const routingGroups = state.routingGroups.values;
      const skillHierarchy = state.skillHierarchy.values;
      const skills = state.skills.values;
      const agentSkills = await gateways.agentSkills.fetchAll();

      const csv = agentUsersListToCsv({
        agentProfiles,
        agentSkills,
        routingGroups,
        skillHierarchy,
        skills,
        teams,
        users,
        allRoles,
      });

      exportUsersToCSV(csv);
    } catch (err) {
      dispatch(toastError('There was an error while download users.'));
    }
  };

agentUsersResource.effects.uploadCsvFile = uploadCsvFile;
agentUsersResource.effects.downloadCsvFile = downloadCsvFile;

export default agentUsersResource;

async function startLoadingRequiredResources(dispatch, state) {
  const promises: any = [];
  if (state.users.loadingState === STATUS_UNSTARTED) {
    promises.push(dispatch(usersResource.effects.fetchAll()));
  }
  if (state.agentProfile.loadingState === STATUS_UNSTARTED) {
    promises.push(dispatch(agentProfilesResource.effects.fetchAll()));
  }
  if (state.teams.loadingState === STATUS_UNSTARTED) {
    promises.push(dispatch(teamsResource.effects.fetchAll()));
  }
  if (state.routingGroups.loadingState === STATUS_UNSTARTED) {
    promises.push(dispatch(routingGroupsResource.effects.fetchAll()));
  }
  if (state.skillHierarchy.loadingState === STATUS_UNSTARTED) {
    promises.push(dispatch(skillHierarchyResource.effects.fetchAll()));
  }

  return Promise.all(promises);
}
